import React, { useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { ViewNoteModal } from "../modals";
import usePolicyActivity from "./usePolicyActivity";

const SectionCard = styled(Box)({
  backgroundColor: colors.white,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  padding: padding.medium1,
});

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.medium1,
  marginBottom: gaps.small1,
  paddingBottom: gaps.xsmall2,
  borderBottom: `1px solid ${colors.grey09}`,
});

const SectionTitle = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const HeaderCell = styled(TableCell)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.grey11,
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  padding: `${padding.xsmall2} ${padding.small1}`,
});

const BodyCell = styled(TableCell)({
  fontSize: fontSize.small,
  color: colors.black,
  padding: `${padding.xsmall2} ${padding.small1}`,
});

function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return "---";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

interface HistorySectionProps {
  record: PolicyManagementRecord;
}

const HistorySection = ({ record }: HistorySectionProps) => {
  // Activity is sourced from the backend (GetPolicyActivityListByTransaction).
  const { activity } = usePolicyActivity(record.PolicyTransactionID);
  const [open, setOpen] = useState(true);
  const [viewNote, setViewNote] = useState<string | undefined>(undefined);
  const [viewNoteOpen, setViewNoteOpen] = useState(false);

  const handleViewNote = (note: string | undefined) => {
    setViewNote(note);
    setViewNoteOpen(true);
  };

  return (
    <SectionCard>
      <SectionHeader
        sx={{
          cursor: "pointer",
          borderBottom: open ? `1px solid ${colors.grey09}` : "none",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <SectionTitle>History</SectionTitle>
        <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          {open ? (
            <ExpandLessIcon sx={{ color: colors.blue15 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: colors.blue15 }} />
          )}
        </Box>
      </SectionHeader>

      <Collapse in={open} unmountOnExit>
      {activity.length === 0 ? (
        <Typography
          sx={{
            fontSize: fontSize.small,
            color: colors.grey11,
            padding: `${padding.small1} 0`,
          }}
        >
          No activity recorded for this record.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <HeaderCell>Activity</HeaderCell>
                <HeaderCell>User</HeaderCell>
                <HeaderCell>Timestamp</HeaderCell>
                <HeaderCell>Source</HeaderCell>
                <HeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {activity.map((a, i) => {
                const title = a.description || a.policyActivityTypeCode;
                return (
                  <TableRow key={`${a.at}-${i}`}>
                    <BodyCell>{title}</BodyCell>
                    <BodyCell>{a.by || "---"}</BodyCell>
                    <BodyCell>{formatDateTime(a.at)}</BodyCell>
                    <BodyCell>{a.notes || "---"}</BodyCell>
                    <BodyCell align="right">
                      <Tooltip title="View note" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleViewNote(a.notes)}
                          sx={{ color: colors.blue01 }}
                        >
                          <StickyNote2Icon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </BodyCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
      </Collapse>

      <ViewNoteModal
        open={viewNoteOpen}
        onClose={() => setViewNoteOpen(false)}
        note={viewNote}
      />
    </SectionCard>
  );
};

export default HistorySection;
